import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Images, MapPin, Calendar } from "lucide-react";
import { api, BASE } from "../../services/api";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

const TABS = [
    { val: "all",       label: "Všetko"       },
    { val: "koncerty",  label: "Koncerty"      },
    { val: "firemne",   label: "Firemné"       },
    { val: "osvetlenie",label: "Osvetlenie"    },
    { val: "led",       label: "LED & projekcia"},
    { val: "ozvucenie", label: "Ozvučenie"     },
];

const CAT_LABELS = {
    koncerty:   "Koncerty",
    firemne:    "Firemné",
    osvetlenie: "Osvetlenie",
    led:        "LED & projekcia",
    ozvucenie:  "Ozvučenie",
};

function resolveImg(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function bestImage(ref) {
    return resolveImg(ref.image_url || (ref.images && ref.images[0]) || ref.hero_image);
}


function SkeletonCard({ tall }) {
    return (
        <div className={`animate-pulse rounded-2xl bg-nova-surface/40 border border-white/4 ${tall ? "md:col-span-2 md:row-span-2" : ""}`} />
    );
}

export default function Gallery() {
    const [items, setItems]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        api.getReferencie()
            .then(data => setItems(data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const featured = items
        .filter(r => r.featured && bestImage(r))
        .sort((a, b) => (a.featured_order ?? 99) - (b.featured_order ?? 99));
    const filtered = filter === "all" ? featured : featured.filter(r => r.category === filter);

    return (
        <section
            id="gallery"
            className="relative py-24 sm:py-32 px-6 sm:px-12 bg-nova-black overflow-hidden"
            data-testid="gallery-section"
        >
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-4">
                            / 04 — Realizácie
                        </div>
                        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] max-w-3xl">
                            Momenty, ktoré sme{" "}
                            <span className="text-nova-cyan">technicky</span>{" "}
                            pokryli
                        </h2>
                    </motion.div>
                </div>

                {/* Filters + link */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
                    <Tabs value={filter} onValueChange={setFilter}>
                        <TabsList className="flex flex-wrap h-auto bg-transparent gap-2 p-0 justify-start" data-testid="gallery-tabs">
                            {TABS.map(t => (
                                <TabsTrigger
                                    key={t.val}
                                    value={t.val}
                                    className="rounded-sm px-5 py-2 text-sm font-mono uppercase tracking-wide border border-white/8 bg-transparent text-zinc-500 data-[state=active]:border-nova-cyan/60 data-[state=active]:text-nova-cyan data-[state=active]:bg-nova-cyan/5 transition-all"
                                    data-testid={`gallery-tab-${t.val}`}
                                >
                                    {t.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    <Link
                        to="/referencie"
                        className="inline-flex items-center gap-2 text-sm font-medium text-nova-cyan hover:text-white transition-colors shrink-0 group"
                    >
                        Všetky referencie
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>

                {/* Grid */}
                <div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[200px] md:auto-rows-[260px]"
                    data-testid="gallery-grid"
                >
                    {loading ? (
                        [0,1,2,3,4,5,6,7,8].map(i => <SkeletonCard key={i} tall={i % 5 === 0} />)
                    ) : filtered.length === 0 ? (
                        <div className="col-span-full flex items-center justify-center py-20 text-zinc-600 text-sm font-mono">
                            {filter === "all" ? "Žiadne referencie označené pre hlavnú stránku" : "Žiadne referencie v tejto kategórii"}
                        </div>
                    ) : (
                        <AnimatePresence mode="popLayout">
                            {filtered.slice(0, 9).map((ref, i) => (
                                <GalleryCard key={ref.id} ref_={ref} index={i} tall={i % 5 === 0} />
                            ))}
                        </AnimatePresence>
                    )}
                </div>

                {/* CTA if many items */}
                {!loading && filtered.length > 9 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-10 text-center"
                    >
                        <Link
                            to="/referencie"
                            className="inline-flex items-center gap-2 h-11 px-7 rounded-xl border border-nova-cyan/30 text-nova-cyan text-sm font-medium hover:bg-nova-cyan/8 transition-all"
                        >
                            Zobraziť všetky ({filtered.length})
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </motion.div>
                )}
            </div>
        </section>
    );
}

function GalleryCard({ ref_, index, tall }) {
    const img = bestImage(ref_);
    const photoCount = (ref_.images?.length || 0) + (ref_.hero_image ? 1 : 0);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.3, delay: index * 0.03 }}
            className={`relative overflow-hidden rounded-2xl group cursor-pointer border border-white/5 ${
                tall ? "md:col-span-2 md:row-span-2" : ""
            }`}
            data-testid={`gallery-item-${index}`}
        >
            <Link to={`/referencie/${ref_.id}`} className="block w-full h-full">
                {img ? (
                    <img
                        src={img}
                        alt={ref_.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full bg-nova-surface/60" />
                )}

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-nova-black via-nova-black/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                {/* Photo count badge */}
                {photoCount > 1 && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2 py-1 border border-white/10">
                        <Images className="w-3 h-3 text-nova-cyan" />
                        <span className="font-mono text-[10px] text-zinc-300">{photoCount}</span>
                    </div>
                )}

                {/* Info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 translate-y-2 group-hover:translate-y-0 transition-transform">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan mb-1">
                        {CAT_LABELS[ref_.category] || ref_.category}
                    </div>
                    <div className="font-heading text-sm sm:text-base font-bold text-white leading-snug">
                        {ref_.title}
                    </div>
                    {(ref_.location || ref_.client) && (
                        <div className="mt-1.5 flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            {ref_.location && (
                                <span className="flex items-center gap-1 text-zinc-400 text-[10px] font-mono">
                                    <MapPin className="w-2.5 h-2.5" />{ref_.location}
                                </span>
                            )}
                            {ref_.event_date && (
                                <span className="flex items-center gap-1 text-zinc-400 text-[10px] font-mono">
                                    <Calendar className="w-2.5 h-2.5" />{ref_.event_date}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Hover ring */}
                <div className="absolute inset-0 ring-1 ring-inset ring-nova-cyan/0 group-hover:ring-nova-cyan/40 transition-all" />
            </Link>
        </motion.div>
    );
}
